import { PackageIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { AnimatePresence, motion } from "framer-motion";

import type { AiPack } from "@/client/services/ai-credit/api";
import { useAccountCurrency } from "@/client/services/currency/currency";

import { PackCard } from "./pack-card";

type Props = {
  packs?: AiPack[];
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  onBuy: (pack: AiPack) => void;
};

const DisplayCurrencyBadge = () => {
  const { context } = useAccountCurrency();

  return (
    <span className="hidden rounded-full border border-indigo-100 bg-white px-3 py-1.5 text-[9px] font-bold text-indigo-700 sm:inline-flex">
      Prix affichés en {context?.currency ?? "…"}
    </span>
  );
};

export const PacksSection = ({ packs, loading, error, onRetry, onBuy }: Props) => (
  <section id="ai-packs" aria-labelledby="ai-packs-title" className="scroll-mt-5">
    <div className="mb-4 flex items-end justify-between gap-4">
      <div>
        <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-emerald-600">
          Recharge
        </p>
        <h2
          id="ai-packs-title"
          className="mt-1 text-lg font-bold tracking-tight text-[#071451] sm:text-xl"
        >
          Choisissez votre pack
        </h2>
        <p className="mt-1 text-[11px] text-slate-500 sm:text-xs">
          Un paiement unique, sans abonnement automatique.
        </p>
      </div>
      <DisplayCurrencyBadge />
    </div>

    <AnimatePresence mode="wait">
      {loading ? (
        <motion.div
          key="loading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
        >
          {[0, 1, 2].map((item) => (
            <div
              key={item}
              className="h-[280px] animate-pulse rounded-2xl border border-slate-200 bg-white"
            />
          ))}
        </motion.div>
      ) : error || !packs?.length ? (
        <motion.div
          key="empty"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex min-h-48 flex-col items-center justify-center rounded-2xl border border-dashed border-indigo-200 bg-white px-5 text-center"
        >
          <span className="grid size-10 place-items-center rounded-xl bg-indigo-50 text-indigo-600">
            <PackageIcon size={21} weight="duotone" />
          </span>
          <p className="mt-3 text-xs font-bold text-[#071451]">
            Aucun pack disponible pour le moment
          </p>
          <p className="mt-1 text-[10px] text-slate-500">
            Revenez dans quelques instants ou actualisez la liste.
          </p>
          {Boolean(error) && (
            <Button variant="outline" size="sm" className="mt-4 rounded-lg" onClick={onRetry}>
              <WarningCircleIcon size={15} className="mr-1.5" /> Réessayer
            </Button>
          )}
        </motion.div>
      ) : (
        <motion.div
          key="packs"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
        >
          {packs.map((pack, index) => (
            <PackCard key={pack.id} pack={pack} index={index} onBuy={onBuy} />
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  </section>
);
